import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('fityh_token'));
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState(localStorage.getItem('fityh_lang') || 'en');
  const [theme, setTheme] = useState(localStorage.getItem('fityh_theme') || 'dark');

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    localStorage.setItem('fityh_lang', language);
  }, [language]);

  useEffect(() => {
    localStorage.setItem('fityh_theme', theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      if (response.data.language) {
        setLanguage(response.data.language);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only logout if token is actually invalid/expired (401 error)
      // Don't logout for network errors or server issues
      if (error.response?.status === 401) {
        console.log('Token expired or invalid, logging out');
        logout();
      } else {
        // Keep user logged in for network issues, try again later
        console.log('Network error, keeping session alive');
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('fityh_token', newToken);
    setToken(newToken);
    setUser(userData);
    if (userData.language) {
      setLanguage(userData.language);
    }
    // Update online status
    try {
      await axios.post(`${API}/users/online`, {}, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
    } catch (e) {}
    return userData;
  };

  // Login with token directly (for OTP login)
  const loginWithToken = async (newToken, userData) => {
    localStorage.setItem('fityh_token', newToken);
    setToken(newToken);
    setUser(userData);
    if (userData.language) {
      setLanguage(userData.language);
    }
    // Update online status
    try {
      await axios.post(`${API}/users/online`, {}, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
    } catch (e) {}
    return userData;
  };

  const register = async (email, username, password, displayName) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      username,
      password,
      display_name: displayName || username
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('fityh_token', newToken);
    setToken(newToken);
    setUser(userData);
    // Update online status
    try {
      await axios.post(`${API}/users/online`, {}, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
    } catch (e) {}
    return userData;
  };

  const logout = async () => {
    // Update offline status before logout
    if (token) {
      try {
        await axios.post(`${API}/users/offline`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {}
    }
    localStorage.removeItem('fityh_token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API}/auth/profile`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(response.data);
    if (data.language) {
      setLanguage(data.language);
    }
    return response.data;
  };

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const value = {
    user,
    token,
    loading,
    language,
    theme,
    login,
    loginWithToken,
    register,
    logout,
    updateProfile,
    setLanguage,
    toggleTheme,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
